import React from 'react';
import { compareAsc } from 'date-fns';
import { useForm } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import { useFieldArray } from 'react-final-form-arrays';
import { useDispatch } from 'react-redux';

import { Advice } from 'types/Checklist';
import AdviceStatus from 'types/AdviceStatus';
import type { Extra } from 'types/ExtrasForm';
import FormKeys from 'modules/form/types/FormKeys';
import { ProactiveWork } from 'types/RepairOrderForm';
import { b, link } from 'utils/i18nUtils';
import { useSelector } from 'hooks';
import { formatUndefined } from 'utils/formUtils';
import usePreAuthorizedAmounts from 'hooks/usePreAuthorizedAmounts';
import { useGetSelfServiceByIdQuery } from 'modules/selfServices/service';
import { computeTotalPrice, roundAmountByCountry } from 'utils/priceUtils';
import { getSelectedSelfServiceId } from 'modules/auth/selectors';
import { getCountry, getValueAddedTax } from 'modules/selfServices/selectors';
import { getAdvicesFunctions } from 'components/Advices';
import useRepairOrder from 'components/RepairOrder/useRepairOrder';
import { openOverviewModal } from 'modules/basket/actions';
import { isOverviewModalOpen as isOverviewModalOpenSelector } from 'modules/basket/selectors';

import List from './List';
import TotalLine from './TotalLine';
import RepairOrderList from './RepairOrderList';
import BasketTaxDetailsModal from './BasketTaxDetailsModal';

const Content: React.FC = () => {
  const form = useForm();
  const dispatch = useDispatch();

  const vat = useSelector(getValueAddedTax);
  const country = useSelector(getCountry);
  const selfServiceId = useSelector(getSelectedSelfServiceId);
  const isOverviewModalOpen = useSelector(isOverviewModalOpenSelector);

  const { customerPreAuthorizedAmount, hasCustomerPreAuthorizedAmount } = usePreAuthorizedAmounts();
  const { data: selfService } = useGetSelfServiceByIdQuery({ id: selfServiceId });

  const {
    value: extras = [],
    update: updateExtra,
  } = useFieldArray<Extra>(`${FormKeys.EXTRAS}.extras`, {
    subscription: { value: true }, format: formatUndefined,
  }).fields;

  const {
    value: proactiveWorks = [],
    update: updateProactiveWork,
  } = useFieldArray<ProactiveWork>(`${FormKeys.REPAIR_ORDER}.proactiveWorks`, {
    subscription: { value: true }, format: formatUndefined,
  }).fields;

  const acceptedExtras = React.useMemo(
    () => extras
      .filter((extra) => extra.status === AdviceStatus.ACCEPTED)
      .toSorted((a, b) => compareAsc(a.answeredDate ?? Date.now(), b.answeredDate ?? Date.now())),
    [extras],
  );

  const acceptedProactiveWorks = React.useMemo(
    () => proactiveWorks.filter((proactiveWork) => proactiveWork.status === AdviceStatus.ACCEPTED).sort(
      // Already accepted works should go top
      (a, b) => compareAsc(a.answeredDate ?? new Date(), b.answeredDate ?? new Date()),
    ),
    [proactiveWorks],
  );

  const { sortedRepairOrderItems } = useRepairOrder(selfService);

  const nbExtras = acceptedExtras.length;
  const nbRepairOrder = sortedRepairOrderItems.length;
  const nbProactiveWorks = acceptedProactiveWorks.length;
  const nbWorks = nbRepairOrder + nbExtras + nbProactiveWorks;
  const hasData = nbWorks > 0;

  // Only customer payer is counted in the total
  const repairOrderItemsForCustomer = React.useMemo(
    () => sortedRepairOrderItems.filter((repairOrder) => !repairOrder.payerId),
    [sortedRepairOrderItems],
  );

  const totalExtras = React.useMemo(
    () => computeTotalPrice(acceptedExtras, vat),
    [acceptedExtras, vat],
  );

  const totalProactiveWorks = React.useMemo(
    () => computeTotalPrice(acceptedProactiveWorks, vat),
    [acceptedProactiveWorks, vat],
  );

  const totalRepairOrder = React.useMemo(
    () => customerPreAuthorizedAmount ?? computeTotalPrice(repairOrderItemsForCustomer, vat),
    [customerPreAuthorizedAmount, repairOrderItemsForCustomer, vat],
  );

  const total = React.useMemo(
    () => roundAmountByCountry[country?.code](totalExtras + totalProactiveWorks + totalRepairOrder),
    [country?.code, totalExtras, totalProactiveWorks, totalRepairOrder],
  );

  const hiddenRepairOrderPrices = !hasCustomerPreAuthorizedAmount
    && selfService.dealer.hidePreApprovedWorksPrices
    && repairOrderItemsForCustomer.length > 0;

  /**
   * @description This function returns a 'remove function factory'
   * that will in turn return a function that will remove the advice from the list when given an adviceId.
   */
  const getRemoveAdviceFactory = React.useCallback(
    (advices: Array<Advice>, update: (index: number, value: Advice) => void) => (adviceId: string) => () => {
      // When you remove the last advice (of all) and there is no repair order.
      const shouldResetConsent = nbWorks === 1;

      const index = advices.findIndex((extra) => extra.adviceId === adviceId);

      form.batch(() => {
        update(index, {
          ...advices[index],
          status: AdviceStatus.REFUSED,
        });

        if (shouldResetConsent) {
          form.change(`${FormKeys.POLICIES}.basketGTC`, false);
          form.change(`${FormKeys.POLICIES}.basketConsent`, false);
        }
      });
    },
    [form, nbWorks],
  );

  const onShowDetails = () =>{
    dispatch(openOverviewModal());
  };

  const handleRemoveExtra = React.useCallback(
    (adviceId: string) => getRemoveAdviceFactory(extras, updateExtra)(adviceId),
    [getRemoveAdviceFactory, extras, updateExtra],
  );

  const handleRemoveProactiveWork = React.useCallback(
    (adviceId: string) => getRemoveAdviceFactory(proactiveWorks, updateProactiveWork)(adviceId),
    [getRemoveAdviceFactory, proactiveWorks, updateProactiveWork],
  );

  return (
    <>
      <div className="flex flex-col">
        {!hasData && (
          <span className="text-center text-default italic text-xs" data-testid="empty-basket">
            <FormattedMessage
              id="basket.empty"
              defaultMessage="Your basket is empty"
            />
          </span>
        )}
        {hasData && (
          <div>
            {nbRepairOrder > 0 && (<RepairOrderList items={sortedRepairOrderItems} />)}
            {nbProactiveWorks > 0 && (
              <List
                items={acceptedProactiveWorks}
                onRemove={handleRemoveProactiveWork}
                getAdvices={getAdvicesFunctions[FormKeys.REPAIR_ORDER]}
                title={<FormattedMessage id="proactiveWorks.title" defaultMessage="Proactive work" />}
              />
            )}
            {nbExtras > 0 && (
              <List
                items={acceptedExtras}
                onRemove={handleRemoveExtra}
                getAdvices={getAdvicesFunctions[FormKeys.EXTRAS]}
                title={<FormattedMessage id="basket.total.extras" defaultMessage="Check-in extras" />}
              />
            )}
            <div className="h-px w-full bg-neutral-blue-100 my-l" />
            <TotalLine
              price={total}
              className="!text-default font-semibold"
              showHiddenPriceWarning={hiddenRepairOrderPrices}
              title={<FormattedMessage id="basket.total.title" defaultMessage="Total" />}
            />
            <p className="text-center text-xs mt-l">
              <FormattedMessage
                id="basket.modal.disclaimer"
                // eslint-disable-next-line max-len
                defaultMessage="Items without price do not indicate they will be provided free of charge. Their final price has not yet been set, will be determined later and communicated to you accordingly. The total amount may be impacted."
              />
            </p>
            <p className="mt-3 text-center text-xs">
              <FormattedMessage
                id="basket.taxDetails"
                defaultMessage="The total amount includes tax. <b><link>Show details</link></b>"
                values={{
                  b,
                  link: link({
                    className: 'text-xs',
                    onClick: onShowDetails,
                  }),
                }}
              />
            </p>
          </div>
        )}
      </div>
      {isOverviewModalOpen && <BasketTaxDetailsModal/>}
    </>
  );
};

export default Content;
